import jwt from 'jsonwebtoken';

import _ from 'lodash';
import moment from 'moment-timezone';
import { amorphicStatic } from '@haventech/amorphic';
import { Utils as CommonUtils } from '@hit/utils';
import * as uuid from 'uuid';
import isEmail from 'validator/lib/isEmail';

import { CustomEnum, EnumVal } from './interfaces/CustomEnum';
import { DateIsoObj } from './interfaces/DateIsoObj';
import { ViewAddress } from './interfaces/ExamScheduler';
import { Holidays } from './static/Holidays';
import { Product } from './static/Product';
import m from 'moment';
import { DateTime } from 'luxon';
import { ProducerDesignation, CdmProducerDesignations } from '../js/services/CdmService';
import { AgeUtils } from './static/AgeUtils';
import { DateDMY } from '../../coverpath-common/js/interfaces/CoverpathTypes';
import { LoggerSingleton } from './logger/LoggerSingleton';
import * as crypto from 'crypto';
import { UICategory, UILoggerSingleton } from './logger/UILoggerSingleton';
import { Address } from './interfaces/CommonTypes';

export enum DayOfWeek {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

export class Utils extends CommonUtils {
  static toMilliseconds(unit?: string): number {
    unit = unit ? unit.toLowerCase() : 'ms';

    if (unit.match(/^secs?$/)) {
      return 1e3;
    }

    if (unit.match(/^mins?$/)) {
      return 6e4;
    }

    if (unit.match(/^hrs?$/)) {
      return 36e5;
    }

    if (unit.match(/^days?$/)) {
      return 864e5;
    }

    return 1;
  }

  static getRunMode(): string {
    return amorphicStatic.config.nconf.get('runMode') || '';
  }

  static isRunMode(mode: string, subMode?: string, subModes?: string): RegExpMatchArray {
    let regex,
      modeSpec = '';

    if (subMode) {
      regex = new RegExp('^\\s*' + mode + '\\s*:([\\w-]*\\s+)*' + subMode + '(\\s+[\\w-]*)*$', 'i');

      if (subModes && subModes.match(/^\s*!/)) {
        modeSpec = mode + ': ' + subModes.replace(/^\s*!\s*/, '');
      } else if (subModes && subModes.match(/^\s*\+/)) {
        modeSpec = Utils.getRunMode() + ' ' + subModes.replace(/^\s*\+\s*/, '');
      } else {
        modeSpec = Utils.getRunMode();
      }
    } else {
      regex = new RegExp('^\\s*' + mode, 'i');
      modeSpec = Utils.getRunMode() || '';
    }

    return modeSpec.match(regex);
  }

  static isLogLevel(level: string): boolean {
    const levels = amorphicStatic.config.nconf.get('logLevels');

    return !!levels.match(new RegExp(level, 'i'));
  }

  static echo(value) {
    return value;
  }

  // Useful for distinguishing between falsey values.
  // 0 and false are often legitimate IO,
  // while undefined and null often mean no IO was received.
  static exists(value): boolean {
    return value !== undefined && value !== null && value !== '';
  }

  static extend(obj, ...args) {
    if (obj) {
      Array.prototype.slice.call(args, 0).forEach((source) => {
        if (source) {
          for (const prop in source) {
            const descriptor = Object.getOwnPropertyDescriptor(source, prop);

            Object.defineProperty(obj, prop, descriptor);
          }
        }
      });
    }

    return obj;
  }

  // Parses an object using the given reference path, returning the value of the last
  // reference or undefined if the reference path is not valid
  static get(obj: any, path: string): any | undefined {
    const pathArr: string[] = path.split('.');
    let ret = obj;

    // falsy indicates not an object or array
    if (!ret || !(typeof ret === 'object')) {
      return undefined;
    }

    while (pathArr.length > 0) {
      ret = ret[pathArr.shift()];
      if (!ret || !(typeof ret === 'object')) {
        if (pathArr.length === 0) {
          return ret;
        }
        return undefined;
      }
    }

    return ret;
  }

  static milliseconds(value: number, unit: string): number {
    return value * Utils.toMilliseconds(unit);
  }

  static getPreviousDate(date: Date): Date {
    date.setDate(date.getDate() - 1);

    return date;
  }

  static timeDiff(dt1: Date, dt2: Date, unit: string): number {
    return (Number(dt1) - Number(dt2)) / Utils.toMilliseconds(unit);
  }

  static offsetDate(date: Date, offset: number, unit: string): Date {
    unit = unit ? unit.toLowerCase() : '';
    const newDate = new Date(date.getTime());

    if (unit.match(/^days?$/)) {
      newDate.setUTCDate(date.getUTCDate() + offset);
    } else if (unit.match(/^months?$/)) {
      newDate.setUTCMonth(date.getUTCMonth() + offset);
    } else if (unit.match(/^years?$/)) {
      newDate.setUTCFullYear(date.getUTCFullYear() + offset);
    }

    return newDate;
  }

  static getTimeOffset(t1: [number, number]): number {
    const timeDiff = process.hrtime(t1);
    return timeDiff[0] * 1000 + timeDiff[1] / 1000000;
  }

  static dateToIsoObj(date: Date): DateIsoObj {
    if (date) {
      const dt = date.toISOString().split('T');

      return {
        date: dt[0],
        time: dt[1],
      };
    }

    return {
      date: null,
      time: null,
    };
  }

  static isoToDate(dtStr: string, timeStr?: string): Date {
    if (!dtStr) {
      return null;
    }

    if (timeStr) {
      dtStr += 'T' + timeStr;
    }

    return new Date(dtStr);
  }

  static isoToDateStripOffset(timeStr: string): Date {
    timeStr = timeStr.replace(/[-+]\d\d:\d\d/, '');
    return new Date(timeStr);
  }

  static isoToDateWithTimezone(timeStr: string, timezone?: string): Date {
    timeStr = timeStr.replace(/[-+]\d\d:\d\d/, '');
    const utcDate = new Date(timeStr);
    const msOffset = Utils.milliseconds(Utils.tzDate(utcDate, timezone).timezoneOffset(), 'mins');
    return new Date(utcDate.valueOf() - msOffset);
  }

  static mdyToDate(dtStr: string): Date {
    if (!dtStr) {
      return null;
    }

    const dtParts = dtStr.split(/[\/\-]/);

    return new Date(Date.UTC(Number(dtParts[2]), Number(dtParts[0]) - 1, Number(dtParts[1])));
  }

  static ymdToDate(dtStr: string): Date {
    if (!dtStr) {
      return null;
    }

    const dtParts = dtStr.split(/[\/\-]/);

    return new Date(Date.UTC(Number(dtParts[0]), Number(dtParts[1]) - 1, Number(dtParts[2])));
  }

  static tzDate(date: Date, timezone?: string) {
    return {
      dt: (function () {
        if (date) {
          return timezone ? moment(date).tz(timezone) : moment(date);
        }

        return date;
      })(),
      toDate: function (): Date | null {
        return this.dt ? this.dt.toDate() : null;
      },
      getDate: function (): number | null {
        return this.dt ? this.dt.date() : null;
      },
      setDate: function (dayOfMonth: number): void {
        if (this.dt) {
          this.dt.date(dayOfMonth);
        }
      },
      setHour: function (hourOfDay: number): void {
        if (this.dt) {
          this.dt.hour(hourOfDay);
        }
      },
      timezoneOffset: function (): number | null {
        return this.dt ? this.dt.utcOffset() : null;
      },
      format: function (fmt: string): string {
        return this.dt ? this.dt.format(fmt) : '';
      },
      toMdy: function (): string {
        return this.format('L');
      },
      toMy: function (separator?: string): string {
        separator = separator || '/';

        return this.format('MM' + separator + 'YYYY');
      },
      toMdyHm: function (): string {
        return this.format('L LT');
      },
      toYmd: function (separator?: string): string {
        separator = separator || '-';

        return this.format('YYYY' + separator + 'MM' + separator + 'DD');
      },
      toMonthDY: function () {
        return this.dt.format('MMMM D, YYYY');
      },
    };
  }

  //Does not use Moment as its used in client side.
  //removes comma
  //returns example: 11/20/18 11:51 AM EST
  static dateToMdyHMTZ(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    } as const;
    return date.toLocaleString('en-US', options).replace(',', '');
  }

  static dateToMonthDayTimeWithAt(date: Date): string {
    const md: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
    } as const;
    const hm: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
    } as const;
    return date.toLocaleString('en-US', md) + ' at ' + date.toLocaleString('en-US', hm);
  }

  static dateToMdy(date): string {
    return date && date.dt ? date.toMdy() : Utils.tzDate(date).toMdy();
  }

  static dateToDateDMY(date: Date): DateDMY {
    return {
      month: date && date.getMonth() + 1,
      day: date && date.getDate(),
      year: date && date.getFullYear(),
    };
  }

  static dateToMY(date) {
    return date && date.dt ? date.toMy() : Utils.tzDate(date).toMy();
  }

  static dateToMdyHm(date) {
    return date && date.dt ? date.toMdyHm() : Utils.tzDate(date).toMdyHm();
  }

  static dateToYmd(date) {
    return date && date.dt ? date.toYmd() : Utils.tzDate(date).toYmd();
  }

  static yearsBetween(fromDate: Date, toDate: Date): number {
    if (fromDate > toDate) {
      const dt = fromDate;

      fromDate = toDate;
      toDate = dt;
    }

    const toYear = toDate.getUTCFullYear();
    const toMonth = toDate.getUTCMonth();
    const toDay = toDate.getUTCDate();
    const fromYear = fromDate.getUTCFullYear();
    const fromMonth = fromDate.getUTCMonth();
    const fromDay = fromDate.getUTCDate();

    return fromYear === toYear || fromMonth < toMonth || (fromMonth === toMonth && fromDay <= toDay)
      ? toYear - fromYear
      : toYear - fromYear - 1;
  }

  static timeBetween(fromDate: Date, toDate: Date, unit: string): number {
    return Math.floor((Number(toDate) - Number(fromDate)) / Utils.toMilliseconds(unit));
  }

  static daysBetween(fromDate: Date, toDate: Date): number {
    return AgeUtils.daysBetween(fromDate, toDate);
  }

  static daysBetweenIgnoreTime(fromDate: Date, toDate: Date, minutePrecision?: boolean): number {
    let fromDt;
    let toDt;
    if (minutePrecision) {
      fromDt = new Date(
        fromDate.getFullYear(),
        fromDate.getMonth(),
        fromDate.getDate(),
        fromDate.getHours(),
        fromDate.getMinutes()
      );
      toDt = new Date(
        toDate.getFullYear(),
        toDate.getMonth(),
        toDate.getDate(),
        toDate.getHours(),
        toDate.getMinutes()
      );
    } else {
      fromDt = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
      toDt = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
    }

    return Utils.daysBetween(fromDt, toDt);
  }

  static getDayOfMonth(date: Date, day: number, occurance: number): Date {
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    let daysneeded = day - firstDayOfMonth.getDay();

    if (daysneeded < 0) {
      daysneeded = daysneeded + 7;
    }

    const resultedDay = daysneeded + 1 + 7 * (occurance - 1);

    return new Date(date.getFullYear(), date.getMonth(), resultedDay);
  }

  static isInWeekend(date: Date): boolean {
    const day = date.getDay();

    return day === 0 || day === 6;
  }

  static getHolidays(): string[] {
    return Holidays.map((holiday) => {
      return Utils.tzDate(holiday.date).toYmd();
    });
  }

  // This returns a date object in Timezone time.
  // Ex: What would 10AM be in EST?
  static getTargetDate(utcDate: Date, targetTimezone: string): Date {
    const utcMs = Date.UTC(
      utcDate.getFullYear(),
      utcDate.getMonth(),
      utcDate.getDate(),
      utcDate.getHours(),
      utcDate.getMinutes(),
      utcDate.getSeconds(),
      utcDate.getMilliseconds()
    );
    const utcDt = Utils.tzDate(new Date(utcMs), targetTimezone);
    const utcMsInTz = utcMs + Utils.milliseconds(utcDt.timezoneOffset(), 'mins');

    return new Date(utcMsInTz);
  }

  static getBusinessDayOffsetByXDays(startFromDate: Date, offset: number): Date {
    let startFrom;
    let offsetMinutes;
    if (startFromDate) {
      startFrom = startFromDate;
    } else {
      const estTime = Utils.tzDate(Utils.now(), 'America/New_York');
      offsetMinutes = estTime.timezoneOffset();
      startFrom = Utils.offsetTime(estTime.toDate(), offsetMinutes, 'mins');
    }
    let nextDay = startFromDate;
    const holidays = this.getHolidays();

    const offsetDay = offset > 0 ? 1 : -1;
    while (offset !== 0) {
      nextDay = Utils.offsetDate(startFrom, offsetDay, 'days');

      while (this.isHolidayOrWeekend(nextDay, holidays)) {
        nextDay = Utils.offsetDate(nextDay, offsetDay, 'days');
      }

      offset -= offsetDay;
      startFrom = nextDay;
    }

    if (offsetMinutes) {
      nextDay = Utils.offsetTime(nextDay, -offsetMinutes, 'mins');
    }
    return nextDay;
  }

  static isHolidayOrWeekend(theDate, holidays): boolean {
    return Utils.isInWeekend(theDate) || holidays.indexOf(Utils.tzDate(theDate).toYmd()) > -1;
  }

  static findInRange(data, value, inclusive: boolean) {
    for (let i = 0; i < data.length; i += 2) {
      if (value < data[i] || (inclusive && value === data[i])) {
        return data[i + 1];
      }
    }

    return null;
  }

  static generateUuid(): string {
    return uuid.v1();
  }

  static generateUuidV4(): string {
    return uuid.v4();
  }

  static array(obj) {
    return [].concat(obj || []);
  }

  static cleanedArray(arr: any[]): any[] {
    return _.uniq(
      _.filter(arr, (item) => {
        return item !== undefined && item !== null && item !== '';
      })
    ).sort();
  }

  static access(root, path: string, defaultValue?) {
    path = path.charAt(0) === '.' ? path.slice(1) : path;

    if (!Utils.exists(root)) {
      return defaultValue;
    }

    let current = root;
    const fields = path.split('.');

    for (let i = 0; i < fields.length; i++) {
      const fieldValue = current[fields[i]];

      if (Utils.exists(fieldValue)) {
        current = fieldValue;
      } else {
        return defaultValue;
      }
    }

    return current;
  }

  static group(arr: any[], groupSize: number, callback: (grp) => void): void {
    let counter = 0,
      grp = [];

    Utils.array(arr).forEach((e) => {
      if (counter++ < groupSize) {
        grp.push(e);
      } else {
        callback(grp);
        grp = [e];
        counter = 1;
      }
    });

    callback(grp);
  }

  static hasIntersection(arr1: any[], arr2: any[], equalsFuncName: string, propertyName?: string): boolean {
    if (!arr1 || !arr2 || arr1.length === 0 || arr2.length === 0) {
      return false;
    }

    return check(arr1, arr2);

    function check(arr1, arr2) {
      const len1 = arr1.length;
      const len2 = arr2.length;
      let i = 0;
      let j = 0;

      for (i; i < len1; i++) {
        for (j; j < len2; j++) {
          if (equalsFuncName) {
            if (arr1[i][equalsFuncName](arr2[j])) {
              return true;
            }
          }

          if (propertyName) {
            if (arr1[i][propertyName] === arr2[j][propertyName]) {
              return true;
            }
          }

          if (arr1[i] === arr2[j]) {
            return true;
          }
        }
      }

      return false;
    }
  }

  static union(arr1: any[], arr2: any[], equalsFuncName: string, propertyName?: string) {
    if (!arr1 || arr1.length === 0) {
      return Utils.uniq(arr2, equalsFuncName, propertyName);
    }

    if (!arr2 || arr2.length === 0) {
      return Utils.uniq(arr1, equalsFuncName, propertyName);
    }

    return Utils.uniq(_.union(arr1, arr2), equalsFuncName, propertyName);
  }

  static uniq(arr: any[], equalsFuncName?: string, propertyName?: string, isSorted?: boolean): any[] {
    let uniqArr = propertyName ? _.uniqBy(arr, propertyName) : _.uniq(arr);

    if (equalsFuncName) {
      uniqArr = _.reduce(
        arr,
        function (uniqArr, item) {
          const matched = _.reduce(
            uniqArr,
            function (matched, uniqItem) {
              return matched || uniqItem[equalsFuncName](item);
            },
            false
          );

          if (!matched) {
            uniqArr.push(item);
          }

          return uniqArr;
        },
        []
      );
    }

    return uniqArr;
  }

  static fillProperties(firstObj, secondObj, properties) {
    return _.reduce(
      properties,
      (obj, property: string) => {
        const firstObjProperty = obj && obj[property];
        const secondObjProperty = secondObj && secondObj[property];

        if (
          !firstObjProperty ||
          (Utils.array(firstObjProperty).length === 0 &&
            secondObjProperty &&
            Utils.array(secondObjProperty).length !== 0)
        ) {
          obj[property] = secondObjProperty;
        }

        return obj;
      },
      firstObj
    );
  }

  static toCamelCase(...args: string[]): string {
    const str = [].map
      .call(args, (str) => {
        return str.trim();
      })
      .filter((str) => {
        return str.length;
      })
      .join('-');

    if (!str.length) {
      return '';
    }

    if (str.length === 1) {
      return str;
    }

    if (!/[_.\- ]+/.test(str)) {
      if (str === str.toUpperCase()) {
        return str.toLowerCase();
      }

      if (str[0] !== str[0].toLowerCase()) {
        return str[0].toLowerCase() + str.slice(1);
      }

      return str;
    }

    return str
      .replace(/^[_.\- ]+/, '')
      .toLowerCase()
      .replace(/[_.\- ]+(\w|$)/g, function (_m, p1) {
        return p1.toUpperCase();
      });
  }

  static lastElementOfString(str: string, splitBy: string): string {
    const elements = str.split(splitBy);

    return elements[elements.length - 1];
  }

  static toCapitalizedWords(str: string): string {
    if (!str) {
      return '';
    }

    // Separate words by spaces, tabs, or newlines.
    const words = str
      .split(/[\s\t\n\r]+/)
      .filter((word) => {
        return word.length > 0;
      })
      .map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      });

    return words.join(' ');
  }

  static parseCityStateZipFromAddressLine(line: string) {
    const pattern = /([\w\s]+)[,\s]+(\w\w)[,\s]+(\d{5}(-\d{4})?)\s*/;

    const address = typeof line === 'string' ? line.trim().match(pattern) : null;

    return address
      ? {
          city: address[1],
          state: address[2],
          zip: address[3].replace(/-/g, ''),
        }
      : null;
  }

  static formatPhoneNumber(numberString: string): string {
    if (!numberString) {
      return '';
    }

    const len = numberString.length;

    if (len === 10) {
      return format10DigitPhoneNumber(numberString);
    } else if (len === 11) {
      // Country code prefix
      return '+' + numberString.charAt(0) + ' ' + format10DigitPhoneNumber(numberString.slice(1));
    } else {
      return '';
    }

    function format10DigitPhoneNumber(numberString) {
      return '(' + numberString.slice(0, 3) + ') ' + numberString.slice(3, 6) + '-' + numberString.slice(6);
    }
  }

  /**
   * Returns the phone number with optional extension by checking if length > 10 or 11 (depending on presence of country code)
   * ex - 111222333399 ==> (111) 222-3333 x99
   *      1222333444499, true ==> +1 (222) 333-4444 x99
   * @param numberString
   * @param hasCountryCode
   */
  static formatPhoneNumberWithExtension(numberString: string, hasCountryCode: boolean): string {
    if (!numberString || numberString.length < 10) {
      return '';
    }

    const len = numberString.length;
    const phoneNumberLen = hasCountryCode ? 11 : 10;
    const formattedPhoneNumber = Utils.formatPhoneNumber(numberString.substr(0, phoneNumberLen));

    if (len > phoneNumberLen) {
      return formattedPhoneNumber + formatPhoneExtension(numberString.substr(phoneNumberLen));
    }

    return formattedPhoneNumber;

    function formatPhoneExtension(extension: string): string {
      const trimmed = extension.trim();
      if (trimmed.length > 0) {
        return ` x${trimmed}`;
      }
      return '';
    }
  }

  static removePhoneNumberFormat(phoneNumber: string): string {
    return phoneNumber.replace(/[-()]/g, '');
  }

  static formatTelephone(value: string): string {
    if (!value) {
      return '';
    }

    const parts = value.split(' ');

    if (parts[0].length < 10) {
      return value;
    }

    return (
      '(' +
      parts[0].substr(0, 3) +
      ') ' +
      parts[0].substr(3, 3) +
      '-' +
      parts[0].substr(6, 4) +
      (parts[1] && parts[1].length > 0 ? ' x' + parts[1] : '')
    );
  }

  // Get rid of any formatting of the phone number and reformat into (xxx) xx-xxx ext. xxx
  static reformatPhoneNumber(phone: string) {
    if (!phone) {
      return phone;
    }
    // remove all non-number characters from phone
    const digitsOnlyPhone = ('' + phone).replace(/\D/g, '');
    const phoneRegex = new RegExp(/^(\d{3})(\d{3})(\d{4})(\d{0,10})$/);
    const match = digitsOnlyPhone.match(phoneRegex);
    if (!match) {
      return phone;
    }
    let formattedPhone = '(' + match[1] + ') ' + match[2] + '-' + match[3];
    if (match[4]) {
      formattedPhone += ' ext. ' + match[4];
    }
    return formattedPhone;
  }
  /**
   * Prepend 1 to a valid phone number if needed.
   * @param phone - valid phone number
   */
  static addUSCountryCodeToTelephone(phone: string): string {
    phone = phone.replace(/\D/g, '');
    return phone.length === 10 ? '1' + phone : phone;
  }

  /**
   * IMPORTANT: Please be aware that this function converts the input date into your local time date.
   * E.g., if input parameter is "2011-09-24 00:00:00 GMT" (UTC time)
   * and you're at GMT -4 (eastern summer time, NOT daylight saving time),
   * this function returns 2011-09-23 20:00:00 GMT, which is 4 hours behind GMT.
   * As a result, YOU MAY HAVE ONE DAY OFF!!!
   * Consider to use formatUTCDate in formatter.service.ts if you do not want to
   * convert into your local time.
   * E.g., DOB normally should not be converted to your local time.
   * @param value
   */
  static formatDate(value: Date): string {
    if (!value) {
      return '';
    }

    return value.getMonth() + 1 + '/' + value.getDate() + '/' + value.getFullYear();
  }

  static formatDateLong(value: Date): string {
    if (!value) {
      return '';
    }

    const locale = 'en-us';
    const month = value.toLocaleString(locale, { month: 'long' });

    return month + ' ' + value.getDate() + ', ' + value.getFullYear();
  }

  static formatDateLongWithShortMonth(value: Date): string {
    if (!value) {
      return '';
    }

    const locale = 'en-us';
    const month = value.toLocaleString(locale, { month: 'short' });

    return month + ' ' + value.getDate() + ', ' + value.getFullYear();
  }

  static formatMonthWithOrdinalDay(value: Date): string {
    if (!value) {
      return '';
    }
    const month = value.toLocaleString('en-us', { month: 'long' });
    const day = value.getDate();
    return `${month} ${Utils.formatRank(day)}`;
  }

  static formatRank(value?: number): string {
    if (value === null) {
      return '';
    }

    const lastDigit = value % 10;
    const lastTwoDigits = value % 100;

    if (lastDigit === 1 && lastTwoDigits !== 11) {
      return value + 'st';
    }
    if (lastDigit === 2 && lastTwoDigits !== 12) {
      return value + 'nd';
    }
    if (lastDigit === 3 && lastTwoDigits !== 13) {
      return value + 'rd';
    }
    return value + 'th';
  }

  static formatDateAppointment(value: Date, timezone: string, onlyDate = false): string {
    if (!value) {
      return '';
    }

    const LOCALE: string = 'en-us';
    const dayOptions: object = { weekday: 'long', month: 'long', day: 'numeric' };
    const timeOptions: object = { hour: 'numeric', minute: 'numeric' };
    const valueTz = Utils.getTargetDate(value, timezone);
    return onlyDate
      ? `${valueTz.getMonth() + 1}/${value.getDate()}/${value.getFullYear()}`
      : `${valueTz.toLocaleString(LOCALE, dayOptions)} at ${valueTz.toLocaleString(LOCALE, timeOptions)}`;
  }

  static toCapitalCamelCase(str: string): string {
    const camelCased = Utils.toCamelCase(str);

    return camelCased.charAt(0).toUpperCase() + camelCased.slice(1);
  }

  static sparseArraytoString(stringParts: string[], separator?: string): string {
    separator = separator || ', ';

    return stringParts
      .filter((part) => {
        return !!part;
      })
      .join(separator);
  }

  static repeat(characters: string, times: number): string {
    return Array(times + 1).join(characters);
  }

  static formatNumber(value: number, precision?: number): string {
    if (value === null) {
      return '';
    }

    const x = value.toString().split('.');

    precision = precision || (x[1] && x[1].length);

    let x1 = x[0],
      x2 = '';

    if (precision > 0) {
      x[1] = x[1] || '';

      _(precision - x[1].length).times(() => {
        x[1] += '0';
      });

      x2 = '.' + x[1];
    }

    const rgx = /(\d+)(\d{3})/;

    while (rgx.test(x1)) {
      x1 = x1.replace(rgx, '$1' + ',' + '$2');
    }

    return x1 + x2;
  }

  static formatCurrency(value: number | string, decimalPoints?: number, dollarSign: boolean = true): string {
    if (value === null) {
      return '';
    }

    if (isNaN(decimalPoints)) {
      decimalPoints = 2;
    }

    const roundedValue = Utils.round(Number(value), decimalPoints);
    if (roundedValue < 0 && dollarSign) {
      return '-$' + Utils.formatNumber(Math.abs(roundedValue), decimalPoints);
    }
    return (dollarSign ? '$' : '') + Utils.formatNumber(roundedValue, decimalPoints);
  }

  static formatIntCurrency(value: string): string {
    if (value === null) {
      return '';
    }

    const roundedValue = this.round(Number(value), 0);

    return '$' + Utils.formatNumber(roundedValue);
  }

  static convertCurrencyToNumber(value: string): number {
    if (value && typeof value === 'string') {
      return Number(value.replace('$', '').replace(',', ''));
    } else {
      return null;
    }
  }

  static yesNo(value: string | number | boolean): string {
    if (String(value).match(/^(true|yes)$/i)) {
      return 'Yes';
    } else if (String(value).match(/^(false|no)$/i)) {
      return 'No';
    } else if (value === undefined || value === null) {
      return '[No Data]';
    } else {
      return value.toString();
    }
  }

  static replace(origString: string, badSubstring: string, replacementSubstring?: string): string {
    if (!origString || !badSubstring) {
      return origString;
    }

    replacementSubstring = replacementSubstring || '';

    return origString.replace(new RegExp(badSubstring, 'g'), replacementSubstring);
  }

  static round(num: number, precision: number): number {
    const factor = Math.pow(10, precision);

    return Math.round(num * factor) / factor;
  }

  /**
   * Rounds with tie-breaking rule set to round away from zero.
   * This means that positive numbers will be rounded UP on 1/2 values,
   * whereas negative numbers will be rounded DOWN on 1/2  values.
   * This method is also sometimes called Commercial Rounding.
   *
   * @remarks
   * This logic is required for MassMutual Basic Illustrations,
   * specifically the Tabular Values section.
   *
   * @param num The number to be rounded
   * @param precision The number of decimal places to round to
   *
   * @returns `num` rounded to the nearest `precision` number of decimal places,
   * using the "away from zero" tie-breaking rule.
   */
  static roundAwayFromZero(num: number, precision: number): number {
    return Utils.round(Math.abs(num), precision) * Math.sign(num);
  }

  // Note that this func has no input validation.
  static constrain(n: number, low: number, high: number): number {
    if (n <= low) {
      return low;
    }
    if (n >= high) {
      return high;
    }
    return n;
  }

  static average(arr: any[]): number {
    let count = 0;
    const sum = (arr = arr || []).reduce((sum, n) => {
      n = n || n === 0 ? Number(n) : undefined;

      if (isNaN(n)) {
        return sum;
      }

      count++;

      return sum + n;
    }, 0);

    return count > 0 ? sum / count : null;
  }

  static sum(array: any[]): number {
    array = Utils.array(array);

    return array.reduce(function (sum, n) {
      n = Number(n);
      return sum + (n || 0);
    }, 0);
  }

  static base64(data: string, encode: boolean): string {
    if (encode) {
      return Buffer.from(data).toString('base64');
    } else {
      return Buffer.from(data, 'base64').toString('ascii');
    }
  }

  static getParameterByName(name: string, url?: string): string {
    if (!url) {
      url = window.location.href;
    }

    name = name.replace(/[\[\]]/g, '\\$&');

    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
    const results = regex.exec(url);

    if (!results) {
      return null;
    }

    if (!results[2]) {
      return '';
    }

    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  static isTestMode(testMode?: string, testModes?: string): RegExpMatchArray {
    return Utils.isRunMode('test', testMode, testModes);
  }

  static getEnv(): string {
    return amorphicStatic.config.nconf.get('environment').trim();
  }

  static isProdEnv(): boolean {
    return Utils.getEnv().match(/^production(\s*:\s*(.*))?$/i) !== null;
  }

  static isUatEnv(): boolean {
    const env = Utils.getEnv();
    const isUat = env.match(/^uat(\s*:\s*(.*))?$/i) !== null;
    const isPreProd = env.match(/^preproduction(\s*:\s*(.*))?$/i) !== null;
    return isUat || isPreProd;
  }

  static isDemoEnv(): boolean {
    const env = Utils.getEnv();
    const isDemo = env.match(/^demo(\s*:\s*(.*))?$/i) !== null;
    const isPreview = env.match(/^preview(\s*:\s*(.*))?$/i) !== null;
    return isDemo || isPreview;
  }

  /**
   * determines whether the environment is a testing environment (local or CI/sandboxes)
   * do not match on non-testing environments - UAT, Prod, and Demo
   */
  static isTestingEnv(): boolean {
    const isDemo = this.isDemoEnv();
    const isProd = this.isProdEnv();
    const isUAT = this.isUatEnv();
    return !isDemo && !isUAT && !isProd;
  }

  static toBase64(data: string): string {
    return Utils.base64(data, true);
  }

  static fromBase64(data: string): string {
    return Utils.base64(data, false);
  }

  static getAppName(channel: string): string {
    const channelName = channel.toLowerCase();
    let appName;

    switch (channelName) {
      case 'mmdirect':
      case 'lifebridge':
        appName = 'mm-direct';
        break;
      case 'cas':
      case 'mmtrad':
      case 'mmopm':
        appName = 'coverpath-client';
        break;
      default:
        appName = channelName;
    }

    return appName;
  }

  static silenceAU(channel: string) {
    const channelName = Utils.getAppName(channel);

    return amorphicStatic.config.configStore[channelName].get('silenceAU');
  }

  static silenceFU(channel: string) {
    const channelName = Utils.getAppName(channel);

    return amorphicStatic.config.configStore[channelName].get('silenceFU');
  }

  static trim(value) {
    return typeof value === 'string' ? value.trim() : value;
  }

  static jsonToUrlParameters(json): string {
    if (!json || typeof json !== 'object') {
      return '';
    }

    return Object.keys(json)
      .map((key) => {
        let value = json[key];

        if (!value) {
          value = '';
        }

        return encodeURIComponent(key) + '=' + encodeURIComponent(value);
      })
      .join('&');
  }

  static getMillimanMibData(doc) {
    return (
      doc &&
      doc.IntelRX &&
      doc.IntelRX.IntelRXResponse &&
      doc.IntelRX.IntelRXResponse.Order &&
      doc.IntelRX.IntelRXResponse.Order.Application &&
      doc.IntelRX.IntelRXResponse.Order.Application.Applicant &&
      doc.IntelRX.IntelRXResponse.Order.Application.Applicant.MIB
    );
  }

  static calculateBmi(weightLbs: number, heightInches: number): number {
    return Math.floor((weightLbs / heightInches / heightInches) * 703);
  }

  static genPersistorId(row) {
    row.__id__ = 'persistor-' + row._template + '-' + row._id;

    return row;
  }

  static enumObjToArray(enumObj, key: string): any[] {
    const arr = [];
    _.each(enumObj, (item: { code }) => {
      const value = item && (item[key] || item.code);
      if (value) {
        arr.push(value);
      } else {
        UILoggerSingleton.getInstance().warn(
          {
            module: 'Utils',
            function: 'enumObjToArray',
            category: UICategory.request,
            data: {
              key: key,
            },
          },
          'Missing value from the Enum'
        );
      }
    });
    return arr;
  }

  /*
    For a percentile of 100%, there may be a split needed based on number of variables , in equal manner.
    with a fixed percent occupying a certain percentile,we need to split down the remaining equally.
    for example: a 100% with 40% fixed and 3 number of variables need to be fixed as
    [20,20,20];
     for example: a 100% with 45% fixed and 6 number of variables need to be fixed as
     [9.17, 9.17, 9.17, 9.17, 9.16, 9.16]
     */
  static splitRemainderPercentile(fixedPercent: number, numberOfVariables: number): Array<number> {
    if (!numberOfVariables || fixedPercent === 100) {
      return [];
    }
    // Split up the remainder and distribute evenly (round to two decimal places)
    let split = (100 - Math.min(100, fixedPercent)) / numberOfVariables;
    split = Math.floor(split * 100) / 100;
    const splitRemainder = Utils.round(100 - (split * numberOfVariables + fixedPercent), 2);
    let splitRemainderSplitUp = 0,
      remainderAdditions = 0,
      ratioSplitDivideCount = numberOfVariables;

    //calculate rounding reminder to be split up among remaining.
    if (splitRemainder) {
      do {
        ratioSplitDivideCount--;
        const splitUp = (splitRemainder * 100) % ratioSplitDivideCount;
        splitRemainderSplitUp = splitUp === 0 ? splitRemainder / ratioSplitDivideCount : 0;
      } while (splitRemainderSplitUp === 0);
    }
    const results = [];
    while (results.length <= numberOfVariables) {
      let finalSplit = split;
      if (remainderAdditions < ratioSplitDivideCount) {
        finalSplit += splitRemainderSplitUp;
        finalSplit = Utils.round(finalSplit, 2);
        remainderAdditions++;
      }
      results.push(finalSplit);
    }
    return results;
  }

  static getNearestArrayElement(array: Array<number>, value: number, prioritizeUpBound: boolean = true): number {
    let curr = array[0];
    let diff = Math.abs(value - curr);
    for (let val = 0; val < array.length; val++) {
      const newdiff = Math.abs(value - array[val]);

      // 0 vs 50000 for a value of 25000, would give nearest element as 50,000 IF prioritizeUpBound
      if (newdiff === diff && prioritizeUpBound) {
        if (curr < array[val]) {
          curr = array[val];
        }
      } else if (newdiff < diff) {
        diff = newdiff;
        curr = array[val];
      }
    }
    return curr;
  }

  static isValidZipcode(zipcodeToValidate: string): boolean {
    const zipcode = zipcodeToValidate;

    const zipcodeLength = zipcode.length;
    let isValidZipcode = true;

    if (zipcodeLength !== 5 && zipcodeLength !== 9) {
      isValidZipcode = false;
    }

    const allNumbersRegex = /^\d+$/;
    const isAllNumbers = !!zipcode.match(allNumbersRegex);

    if (!isAllNumbers) {
      isValidZipcode = false;
    }

    return isValidZipcode;
  }

  static generateRandomInteger(length: number): number {
    return parseInt('' + Math.random() * Math.pow(10, length), 10);
  }

  static generateRandomIntegerString(length: number): string {
    const num = Utils.generateRandomInteger(length);
    return Utils.fillLeadingZeros(num, length);
  }

  static fillLeadingZeros(num: string | number, length: number): string {
    let str = String(num);
    const zeroSize = length - str.length;
    for (let i = 0; i < zeroSize; i++) {
      str = '0' + str;
    }
    return str;
  }

  static generateRandomSSN(separator: string = '-'): string {
    const str = Utils.generateRandomIntegerString(9);
    return Utils.addSSNSeparator(str, separator);
  }

  static addSSNSeparator(ssn: string | number, separator: string = '-'): string {
    const ssnStr = Utils.fillLeadingZeros(ssn, 9);
    return ssnStr.substr(0, 3) + separator + ssnStr.substr(3, 2) + separator + ssnStr.substr(5, 4);
  }

  static addITINSeparator(itin: string | number, separator: string = '-') {
    const itinStr = Utils.fillLeadingZeros(itin, 9);
    return itinStr.substr(0, 2) + separator + itinStr.substr(2);
  }

  static mappingArray(array: any[] = [], keyName: string) {
    return _.reduce(
      array,
      (map, row) => {
        const key = row[keyName];
        if (key && typeof key === 'string') {
          map[key] = row;
        }
        return map;
      },
      {}
    );
  }

  // obj is {A: {code: 'code1', otherProperty: {}}, B: {code: 'code2', otherProperty: {}}, ...}
  static extractCodesArrayFromEnum(obj) {
    return _.reduce(
      _.values(obj),
      (codes, item) => {
        const code = item.code;
        if (code) {
          codes.push(code);
        }
        return codes;
      },
      []
    );
  }

  static getAgeFromBirthday(birthday: string): number {
    const actualAge = Utils.getActualAge(birthday);
    return actualAge + Utils.halfYearCorrection(birthday);
  }

  static getActualAge(birthday: string | Date): number {
    return AgeUtils.getActualAge(birthday);
  }

  /**
   * Return actual age with fractional year (as opposed to whole years returned by Utils.getActualAge())
   * @param dateOfBirth
   */
  static getActualAgeInFractionalYears(dateOfBirth: Date): number {
    //ignore time of day when doing age calculation
    const dobMoment = m(dateOfBirth.toISOString()).startOf('day');
    const nowMoment = m(Utils.now().toISOString()).startOf('day');

    return nowMoment.diff(dobMoment, 'years', true);
  }

  static roundDownToNearestIncrement(value: number, increment: number): number {
    return Math.floor(value / increment) * increment;
  }

  static halfYearCorrection(birthday: string): number {
    return AgeUtils.halfYearCorrection(birthday);
  }

  static findCodeMatch(data: CustomEnum | EnumVal[], code: string) {
    return _.find(_.values(data), { code: code });
  }

  static capitalizeFirstLetter(letters: string): string {
    return letters.charAt(0).toUpperCase() + letters.slice(1);
  }

  private static matchWorkflowResult(resultValue: string = '', resultToMatched: string = '') {
    const matchGroup = resultValue.split(':');
    return !!matchGroup[0] && matchGroup[0] === resultToMatched;
  }

  static workflowResultIsDone(resultValue: string = '') {
    return Utils.matchWorkflowResult(resultValue, 'Done');
  }

  static openFileInNewWindow(content: string, contentType: string): void {
    const atob = window.atob(content);
    const len = atob.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = atob.charCodeAt(i);
    }
    const byteArray = new Uint8Array(bytes);
    const blob = new Blob([byteArray], { type: contentType });
    const fileURL = URL.createObjectURL(blob);
    window.open(fileURL);
  }

  /**
   * *
   * Check if the channel comes under Coverpath platform
   * Revisit once we have the platform concept fully implemented as part of HL-15620
   *
   */
  static isCoverpathPlatform(channelCode: string): boolean {
    return Product.platforms.Coverpath.channels.includes(channelCode);
  }

  /**
   * *
   * a utility function to return the unique combination of one element from each of the input arrays
   *
   * @param arrays list of arrays with type in String or Number
   * @param combine variable to keep current combinations
   * @param finalList a list to keep result during the recursion
   *
   * @returns one array of arrays
   * i.e. arrays =  [1, 2, 3], [a, b, c] ,[Hello, World]
   *
   * output = [[1, a, Hello], [1, a, World], [2, a, Hello], ....]
   *
   */
  static getCombination(
    arrays: Array<Array<number | string>>,
    combine = [],
    finalList = []
  ): Array<Array<number | string>> {
    if (!arrays.length) {
      finalList.push(combine);
    } else {
      arrays[0].forEach((now) => {
        const remainingArrays = arrays.slice(1);
        const copy = combine.slice();
        copy.push(now);
        Utils.getCombination(remainingArrays, copy, finalList);
      });
    }
    return finalList;
  }

  /**
   * Returns a map of amorphic class name to _id... with the intention that this makes logging easier
   */
  static extractIds(...items: { _id: string; amorphicGetClassName(): string }[]): { [key: string]: string } {
    const typeToId = {};
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      //if, for instance, 2 policies are passed in, we want to include both in the output
      const defaultKey = item.amorphicGetClassName();

      if (typeToId[defaultKey]) {
        typeToId[`${defaultKey}-${item._id}`] = item._id;
      } else {
        typeToId[defaultKey] = item._id;
      }
    }

    return typeToId;
  }

  static isEmailString(value: string): boolean {
    return isEmail(value);
  }

  static roundToNearestThousand(input: number): number {
    return Math.round(input / 1000) * 1000;
  }

  /**
   * find the second to last occurrence of a . and split from there to get the second level domain
   *
   * `dev-box1.blackops.dev.havenlife.com` will return `.havenlife.com`
   *
   *  `havenlife.com` will return `havenlife.com`
   *
   *  `notarealurl` with return ``
   */
  static getSecondLevelDomain(serverHost: string): string {
    if (!serverHost) {
      return '';
    }

    const matchResults = serverHost.match(/\./g);
    if (matchResults && matchResults.length > 1) {
      const lastPeriod = serverHost.lastIndexOf('.');
      const secondToLastPeriod = serverHost.lastIndexOf('.', lastPeriod - 1);
      const secondLevelDomain = serverHost.slice(secondToLastPeriod, serverHost.length);
      return secondLevelDomain;
    } else if (matchResults) {
      return serverHost;
    } else {
      return '';
    }
  }

  /**
   * Convert a url into a devbox friendly one if required
   *
   * returns the passed in URL as is if the server is not a devbox
   */
  static convertUrlForDevBoxes(url: string): string {
    let reformedUrl: string = url;
    const serverName: string = amorphicStatic.config.nconf.get('SERVER_NAME');
    //account for dev-boxes and convert the url from localhost
    if (serverName && serverName.includes('dev')) {
      const endOfURL: RegExpExecArray | null = /.com/g.exec(serverName);
      const index = endOfURL ? endOfURL.index : null;
      if (index) {
        //Combine the url's suffix with the dev-box url
        const portInUrl = /:[0-9]+/g.exec(url);
        reformedUrl = `https://${serverName.slice(0, index + 4)}${
          portInUrl && portInUrl.index ? url.slice(portInUrl.index) : ''
        }`;
      }
    }
    return reformedUrl;
  }

  static buildSessionKey(sessionId: string) {
    const ver = amorphicStatic.config.nconf.get('ver');
    const sessionKey = `sess_v${ver}${sessionId}`;
    return sessionKey;
  }
  /*
  Extract DB ID from a persistor object's __id__.
  Used by front end code where an object's DB ID is not available.
   */
  static extractPersistorObjectDbId(__id__: string): string {
    if (!__id__ || __id__.indexOf('-') === -1 || __id__.split('-').length !== 3 || __id__.split('-')[2].length < 15) {
      return null;
    }
    return __id__.split('-')[2];
  }

  static convert12hAppsTimestrTo24h(timeStr: string): string {
    const hoursStr = timeStr.match(/^(\d+)/)[0];
    const hours = Number(hoursStr) % 12;
    const minutes = timeStr.match(/:(\d+)/)[1];
    if (timeStr.indexOf('PM') > 0 || timeStr.indexOf('pm') > 0) {
      return `${hours + 12}:${minutes}`;
    } else {
      return `${String(hours).padStart(2, '0')}:${minutes}`;
    }
  }
  //ISO date time string
  static createDateWithTimeZone(IsoDateTimeStr: string, timezone: string): Date {
    return DateTime.fromISO(IsoDateTimeStr, { zone: timezone }).toJSDate();
  }

  static formatAddress(
    address: Address,
    fields: string[][] = [['street', 'line1', 'line2'], ['city'], ['state', 'zip'], ['country']],
    delimiter = ', '
  ): string {
    function cleanupValue(value: string): string {
      return value ? value.trim() : '';
    }
    function joinValue(arr: string[], obj: Address): string {
      return arr
        .map((value) => cleanupValue(obj[value]))
        .filter((value) => value !== '')
        .join(' ')
        .trim();
    }
    return fields
      .map((values) => joinValue(values, address))
      .filter((value) => value !== '')
      .join(delimiter)
      .trim();
  }

  static formatViewAddress(
    address: ViewAddress,
    fields: string[][] = [['line1', 'line2'], ['city'], ['state', 'zip'], ['country']],
    delimiter = ', '
  ): string {
    function cleanupValue(value: string): string {
      return value ? value.trim() : '';
    }
    function joinValue(arr: string[], obj: ViewAddress): string {
      return arr
        .map((value) => cleanupValue(obj[value]))
        .filter((value) => value !== '')
        .join(' ')
        .trim();
    }
    return fields
      .map((values) => joinValue(values, address))
      .filter((value) => value !== '')
      .join(delimiter)
      .trim();
  }

  static isDateInTheFuture(date: Date): boolean {
    return m(date).isAfter(m(), 'day');
  }

  static convertCdmDesignationsArrayToStringArray(designations: Array<ProducerDesignation>): Array<string> {
    const dictionary = {
      blueChipCircle: 'Society 1851',
      specialCare: 'Special Care',
    };
    const designationOutputArray: Array<string> = [];
    designations.forEach((designationObject) => {
      const designationName = dictionary[Object.keys(designationObject)[0]];
      const designationValue = designationObject[Object.keys(designationObject)[0]];
      if (designationValue) {
        designationOutputArray.push(designationName);
      }
    });
    return designationOutputArray;
  }

  static returnDefaultCdmDesignations(producerIds: string[]): CdmProducerDesignations {
    const result = {};
    producerIds.forEach((producerId) => {
      if (!result[producerId]) {
        result[producerId] = {
          producerId,
          blueChipCircle: false,
          specialCare: false,
        };
      }
    });
    return result;
  }

  static isJson(str: string): boolean {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  }

  static getPaymentTypeBasedOnPA(paymentType: string, isAutoPay: boolean) {
    if (paymentType === 'BankAccount') {
      return isAutoPay ? 'ACH (AutoPay)' : 'ACH';
    } else if (paymentType === 'PayByCheck') {
      return 'Check';
    } else {
      return '';
    }
  }

  static policyPaymentFrequencyToText(frequency: string): string {
    if (frequency === 'annual') {
      return '/year';
    } else if (frequency === 'semiannual') {
      return ' semi-annually';
    } else if (frequency === 'quarterly') {
      return '/quarter';
    } else {
      return '/month';
    }
  }

  static policyPaymentFrequencyForExport(frequency: string): string {
    if (frequency === 'Annual') {
      return 'Annual';
    } else if (frequency === 'SemiAnnual') {
      return 'Semi-annual';
    } else if (frequency === 'Quarterly') {
      return 'Quarterly';
    } else {
      return 'Monthly';
    }
  }

  /**
   * class A {}
   * class B extends A {}
   * class C extends A {}
   * class D extends B {}
   *
   * extendsClass(new D(), 'A') === true
   * extendsClass(new D(), 'B') === true
   * extendsClass(new C(), 'A') === true
   * extendsClass(new C(), 'B') === false
   *
   * @param obj
   * @param className
   */
  static extendsClass(obj: object, className: string) {
    const prototype = Object.getPrototypeOf(obj);
    if (!prototype) {
      return false;
    }
    if (!prototype.constructor) {
      return false;
    }
    if (prototype.constructor.name === className) {
      return true;
    }
    return Utils.extendsClass(prototype, className);
  }

  static parseBoolean(str: string): boolean | null {
    if (typeof str === 'boolean') {
      return str;
    } else if (typeof str === 'string') {
      if (str.toLowerCase() === 'true') {
        return true;
      } else if (str.toLowerCase() === 'false') {
        return false;
      }
    }
    return null;
  }

  static equalsIgnoreCase(str1: string, str2: string): boolean {
    if (!str1 && !str2) {
      return str1 === str2;
    } else if (!str1 || !str2) {
      return false;
    }
    return str1.toLowerCase() === str2.toLowerCase();
  }

  static attachBearerTokenForDevOnlyRoutes(options) {
    if (this.isTestingEnv()) {
      options.headers = _.extend(options.headers || {}, {
        'Content-Type': 'application/json',
        'authorization': 'Bearer ' + amorphicStatic.config.nconf.get('cpApiGatewayJWTToken'),
        ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
      });
    } else {
      return options;
    }
  }

  static getAuthTokenForPdfstream() {
    if (this.isTestingEnv()) {
      return amorphicStatic.config.nconf.get('cpApiGatewayJWTToken');
    } else {
      const docufyToken = amorphicStatic.config.nconf.get('docufyToken');
      return jwt.sign({}, docufyToken);
    }
  }

  static getHash(content: string | Buffer): string {
    return crypto.createHash('md5').update(content).digest('hex');
  }

  static getWholeNumberAndDecimalFromNumber(
    num: number,
    precision: number = 2
  ): { wholeNumber: string; decimal: string } {
    const split = this.round(num, precision).toString().split('.');
    const decimal = split[1] ? (split[1].length === 1 ? `${split[1]}0` : split[1]) : null;
    const wholeNumber = split[0];
    return {
      wholeNumber,
      decimal,
    };
  }

  // If the value: Date passed in is in EST, then this function just simply format the EST time into M/D/YYYY
  static formatLocalDateNoZeros(value: Date): string {
    if (!value) {
      return '';
    }

    return m(value).format('M/D/YYYY');
  }

  static formatExamOrderingProcess(examOptOut: boolean): string {
    return examOptOut ? 'Manual' : 'Automatic';
  }
}
